import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import features from "@/config/features";
import {
    Car,
    ArrowLeft,
    BookOpen,
    ChevronRight,
    CreditCard,
    User,
    LogOut,
    Menu,
    X,
    Crown,
    AlertCircle,
    Video,
    Target,
    Home,
    Loader2,
    CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const chapters = [
    { id: 1, title: "Reguli generale", count: 35 },
    { id: 2, title: "Indicatoare și marcaje", count: 203 },
    { id: 3, title: "Semnalele luminoase și sonore", count: 44 },
    { id: 4, title: "Semnalele polițiștilor", count: 54 },
    { id: 5, title: "Poziția în timpul mersului", count: 83 },
    { id: 6, title: "Depășirea", count: 70 },
    { id: 7, title: "Viteza și distanța între vehicule", count: 57 },
    { id: 8, title: "Reguli referitoare la manevre", count: 33 },
    { id: 9, title: "Intersecții și prioritate de trecere", count: 131 },
    { id: 10, title: "Trecerea la nivel cu calea ferată", count: 31 },
    { id: 11, title: "Oprirea, staționarea și parcarea", count: 70 },
    { id: 12, title: "Circulația pe autostradă", count: 22 },
    { id: 13, title: "Obligațiile conducătorilor de autovehicule", count: 52 },
    { id: 14, title: "Sancțiuni și contravenții", count: 89 },
    { id: 15, title: "Noțiuni de mecanică", count: 54 },
    { id: 16, title: "Măsuri de prim ajutor", count: 60 },
    { id: 17, title: "Conducerea preventivă", count: 19 },
    { id: 18, title: "Conducerea ecologică", count: 54 },
];

export default function ChapterTestsPage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [progress, setProgress] = useState({});
    const [loadingProgress, setLoadingProgress] = useState(true);

    const isSubscriptionActive =
        user?.subscription_status === "active" || user?.role === "admin";

    const hasStandardAccess =
        user?.role === "admin" ||
        (user?.subscription_status === "active" &&
            ["standard", "premium"].includes((user?.subscription_plan || "").toLowerCase()));

    useEffect(() => {
        fetchProgress();
    }, []);

    const fetchProgress = async () => {
        setLoadingProgress(true);
        try {
            const response = await axios.get(`${API}/chapter-quizzes/progress`);
            setProgress(response.data || {});
        } catch (error) {
            console.error("Error fetching progress:", error);
        } finally {
            setLoadingProgress(false);
        }
    };

    const allSidebarLinks = [
        { name: "Dashboard", path: "/dashboard", icon: Home },
        {
            name: "Cursuri Video",
            path: "/cursuri",
            icon: Video,
            requiresSubscription: true,
            feature: "videoCourses",
        },
        {
            name: "Teste DRPCIV",
            path: "/teste-drpciv",
            icon: Target,
            requiresSubscription: false,
        },
        {
            name: "Teste Antrenament",
            path: "/teste",
            icon: Target,
            requiresSubscription: true,
        },
        { name: "Abonamente", path: "/abonamente", icon: CreditCard },
    ];

    const sidebarLinks = allSidebarLinks.filter(
        (link) => !link.feature || features[link.feature]
    );

    const handleLogout = () => {
        logout();
        navigate("/");
        toast.success("Te-ai deconectat cu succes");
    };

    const goToChapterQuiz = (chapterId) => {
        if (!hasStandardAccess) {
            toast.error("Disponibil doar în pachetul Standard sau Premium");
            navigate("/abonamente");
            return;
        }
        navigate(`/teste/capitole/ch-${chapterId}`);
    };

    const getChapterProgress = (chapterId) => {
        const chapterKey = `ch-${chapterId}`;
        const chapterProgress = progress[chapterKey];
        if (chapterProgress) {
            return {
                solved: chapterProgress.solved || 0,
                total: chapterProgress.total || chapters.find(c => c.id === chapterId)?.count || 0,
                bestScore: chapterProgress.best_score || 0,
                attempts: chapterProgress.attempts || 0
            };
        }
        return {
            solved: 0,
            total: chapters.find(c => c.id === chapterId)?.count || 0,
            bestScore: 0,
            attempts: 0
        };
    };

    return (
        <div className="min-h-screen bg-slate-100" data-testid="chapter-tests-page">
            <div className="lg:hidden bg-white border-b sticky top-0 z-40">
                <div className="flex items-center justify-between p-4">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                            <Car className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
                    </Link>
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
                        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            <div className="flex">
                <aside
                    className={`
            fixed lg:sticky top-0 left-0 h-screen w-64 bg-slate-900 text-white z-50
            transform transition-transform duration-300 lg:transform-none
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          `}
                >
                    <div className="p-6 border-b border-slate-700">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="relative">
                                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                                    <Car className="w-6 h-6 text-white" />
                                </div>
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full" />
                            </div>
                            <span className="font-bold text-lg font-['Archivo']">AUTO CORA</span>
                        </Link>
                    </div>

                    <nav className="p-4 space-y-2">
                        {sidebarLinks.map((link) => {
                            const isDisabled = link.requiresSubscription && !isSubscriptionActive;

                            return (
                                <Link
                                    key={link.path}
                                    to={isDisabled ? "/abonamente" : link.path}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                    ${window.location.pathname === link.path
                                            ? "bg-blue-600 text-white"
                                            : "text-slate-300 hover:bg-slate-800"
                                        }
                    ${isDisabled ? "opacity-50" : ""}
                  `}
                                >
                                    <link.icon className="w-5 h-5" />
                                    <span>{link.name}</span>
                                    {isDisabled && <Crown className="w-4 h-4 ml-auto text-yellow-400" />}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{user?.name}</p>
                                <p className="text-sm text-slate-400 truncate">{user?.email}</p>
                            </div>
                        </div>
                        <Button
                            onClick={handleLogout}
                            variant="outline"
                            className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 bg-transparent"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Deconectare
                        </Button>
                    </div>
                </aside>

                {sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                <main className="flex-1 p-4 lg:p-8 min-h-screen">
                    <div className="max-w-6xl mx-auto">
                        {!hasStandardAccess && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mb-6"
                            >
                                <Card className="border-yellow-200 bg-yellow-50">
                                    <CardContent className="p-4 flex items-center justify-between flex-wrap gap-4">
                                        <div className="flex items-center gap-3">
                                            <AlertCircle className="w-6 h-6 text-yellow-600" />
                                            <div>
                                                <p className="font-medium text-yellow-800">Acces restricționat</p>
                                                <p className="text-sm text-yellow-700">
                                                    Chestionarele pe capitole sunt disponibile doar pentru abonament Standard sau Premium.
                                                </p>
                                            </div>
                                        </div>
                                        <Link to="/abonamente">
                                            <Button className="bg-yellow-600 hover:bg-yellow-700 text-white">
                                                Activează Acum
                                            </Button>
                                        </Link>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        )}

                        <div className="mb-6 flex items-center gap-4">
                            <button
                                onClick={() => navigate("/teste")}
                                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                <span>Înapoi la teste</span>
                            </button>
                        </div>

                        <div className="text-center mb-8">
                            <Badge className="bg-slate-100 text-slate-700 text-base px-4 py-1 mb-3">
                                18 capitole
                            </Badge>
                            <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 font-['Archivo'] mb-2">
                                Chestionare pe capitole
                            </h1>
                            <p className="text-slate-600 text-lg">
                                Exersează separat fiecare capitol
                            </p>
                        </div>

                        {loadingProgress ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                                <span className="ml-3 text-slate-600">Se încarcă progresul...</span>
                            </div>
                        ) : (
                            <div className="grid md:grid-cols-2 gap-4">
                                {chapters.map((chapter, index) => {
                                    const chapterProgress = getChapterProgress(chapter.id);
                                    const hasProgress = chapterProgress.attempts > 0;
                                    const isCompleted = chapterProgress.bestScore >= 80;

                                    return (
                                        <motion.div
                                            key={chapter.id}
                                            initial={{ opacity: 0, y: 16 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.03 }}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => goToChapterQuiz(chapter.id)}
                                                className="w-full text-left"
                                                data-testid={`chapter-${chapter.id}`}
                                            >
                                                <Card className={`border-0 shadow-sm hover:shadow-md transition-all duration-200 ${isCompleted ? 'ring-2 ring-green-500' : ''}`}>
                                                    <CardContent className="p-4 flex items-center justify-between gap-4">
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className={`text-white text-xs font-bold px-2 py-1 rounded-md shrink-0 ${
                                                                isCompleted 
                                                                    ? 'bg-green-600' 
                                                                    : hasProgress 
                                                                        ? 'bg-blue-600' 
                                                                        : 'bg-slate-500'
                                                            }`}>
                                                                {chapterProgress.solved}/{chapter.count}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="font-medium text-slate-900 leading-snug">
                                                                    {chapter.title}
                                                                </p>
                                                                {hasProgress && (
                                                                    <p className="text-xs text-slate-500 mt-0.5">
                                                                        Cel mai bun scor: {chapterProgress.bestScore}%
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {isCompleted && (
                                                                <CheckCircle className="w-5 h-5 text-green-600" />
                                                            )}
                                                            <ChevronRight className="w-5 h-5 text-slate-500 shrink-0" />
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </button>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
